import { createSelector } from "@reduxjs/toolkit";
import { openapi } from "../../../openapi";
import type { IRootState } from "../../app/store";
import type { IRoutingGraph } from "@multiconsult-group/multiplacer-core";
import { routingGraphToPixPaths } from "./routingGrahpUti";
import {
  AUTO_PIPE_ROUTING_UNSAVED_LOCAL_ID_PREFIX,
  selectUnsavedRoutingItems,
} from "./unsavedRoutingSlice";
import { IPixPath } from "@multiconsult-group/multiplacer-ui";
import { selectProjectNo, selectSelectedRoutingGraphId } from "../designSlice";
import { selectAutoPipeRoutingIsRunning } from "../pipeRoutingSlice";

export const EMPTY_ROUTING_GRAPHS: IRoutingGraph[] = [];

export const selectGetRoutingGraphsArgs = (state: IRootState) => {
  const projectNo = selectProjectNo(state);
  const floorName = state.design.floorAndJob?.floorName;

  if (!projectNo || !floorName) {
    return null;
  }

  return {
    projectNo,
    floorName,
  };
};

/** HÃ¤mtar query-resultatet (senaste i cachen) fÃ¶r nuvarande args */
export const selectHttpRoutingGraphs = (state: IRootState): IRoutingGraph[] => {
  const args = selectGetRoutingGraphsArgs(state);
  if (!args) return EMPTY_ROUTING_GRAPHS;

  const graphs =
    openapi.endpoints.getRoutingGraphList.select(args)(state).data ?? EMPTY_ROUTING_GRAPHS;
  return graphs.map((graph) => ({
    ...graph,
    nodes: graph.nodes.map(({ kind: _kind, ...node }) => ({
      kind: "JUNCTION" as const,
      ...node,
    })),
  }));
};

export const selectNrOfRoutingGraphs = createSelector([selectHttpRoutingGraphs], (graphs) => {
  return graphs.length;
});

const selectActiveModule = (state: IRootState) => state.design.activeModule;

export const selectSelectedRoutingGraph = createSelector(
  [selectHttpRoutingGraphs, selectSelectedRoutingGraphId],
  (graphs, selectedRoutingGraphId) => {
    if (!selectedRoutingGraphId) {
      return null;
    }

    return graphs.find((graph) => String(graph._id) === selectedRoutingGraphId) ?? null;
  }
);

export const selectUnsavedRoutingGraphs = createSelector([selectUnsavedRoutingItems], (items) =>
  items.map((item) => item.routingGraph)
);

export const selectAllVisibleRoutingGraphs = createSelector(
  [selectHttpRoutingGraphs, selectUnsavedRoutingGraphs],
  (savedGraphs, unsavedGraphs): IRoutingGraph[] => [...savedGraphs, ...unsavedGraphs]
);

export const selectRoutingPixPaths = createSelector(
  [
    selectHttpRoutingGraphs,
    selectUnsavedRoutingItems,
    selectAutoPipeRoutingIsRunning,
    selectSelectedRoutingGraphId,
    selectActiveModule,
  ],
  (
    savedGraphs,
    unsavedItems,
    autoPipeRoutingIsRunning,
    selectedRoutingGraphId,
    activeModule
  ): IPixPath[] => {
    if (activeModule !== "CEILING") {
      return [];
    }

    const selectedGraph = savedGraphs.find((graph) => String(graph._id) === selectedRoutingGraphId);
    const nonSelectedGraphs = savedGraphs.filter(
      (graph) => String(graph._id) !== selectedRoutingGraphId
    );

    return [
      ...nonSelectedGraphs.flatMap((graph) =>
        routingGraphToPixPaths(graph, {
          strokeColor: selectedRoutingGraphId ? "#8e9aaf" : "#2407dd",
        })
      ),
      ...unsavedItems.flatMap((item) =>
        routingGraphToPixPaths(item.routingGraph, {
          strokeColor: item.localId.startsWith(AUTO_PIPE_ROUTING_UNSAVED_LOCAL_ID_PREFIX)
            ? autoPipeRoutingIsRunning
              ? "#2407dd"
              : "#ffd43b"
            : "#2407dd",
        })
      ),
      ...(selectedGraph
        ? routingGraphToPixPaths(selectedGraph, {
            strokeColor: "#14b8a6",
            strokeWidthMultiplier: 2.2,
          })
        : []),
    ];
  }
);
